import React, { useState } from "react";
import { Copy, Check, X, Wand2 } from "lucide-react";

const TYPE_LABEL = {
  missing_faq_schema: "FAQ Schema",
  not_answer_first: "Answer-First Rewrite",
  section_too_long: "Split Plan",
  stale_content: "Freshness Plan",
  no_llms_txt: "llms.txt",
};

export default function FixModal({ artefact, onClose }) {
  if (!artefact) return null;
  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      data-testid="fix-modal"
      onClick={onClose}
    >
      <div
        className="bg-white border border-zinc-200 rounded-[6px] max-w-4xl w-full max-h-[88vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-5 border-b border-zinc-200">
          <div>
            <div className="label-eyebrow">
              {TYPE_LABEL[artefact.type] || artefact.type}
            </div>
            <h3 className="display mt-1 text-xl font-bold tracking-tight">
              {artefact.title}
            </h3>
          </div>
          <button
            onClick={onClose}
            data-testid="fix-modal-close"
            className="text-zinc-400 hover:text-zinc-900 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <div className="overflow-auto flex-1 p-5">
          <ArtefactBody artefact={artefact} />
        </div>
      </div>
    </div>
  );
}

function ArtefactBody({ artefact }) {
  switch (artefact.type) {
    case "missing_faq_schema":
      return <FaqBody artefact={artefact} />;
    case "not_answer_first":
      return <DiffBody artefact={artefact} />;
    case "section_too_long":
      return <SplitBody artefact={artefact} />;
    case "stale_content":
      return <StaleBody artefact={artefact} />;
    case "no_llms_txt":
      return <LlmsTxtBody artefact={artefact} />;
    default:
      return <pre className="code-block">{artefact.content}</pre>;
  }
}

function CopyBtn({ value, label = "Copy", testid }) {
  const [ok, setOk] = useState(false);
  return (
    <button
      onClick={async () => {
        await navigator.clipboard.writeText(value);
        setOk(true);
        setTimeout(() => setOk(false), 1400);
      }}
      data-testid={testid}
      className="inline-flex items-center gap-1 text-xs border border-zinc-900 px-2.5 py-1 rounded-[4px] hover:bg-zinc-100 transition-colors"
    >
      {ok ? <Check size={12} /> : <Copy size={12} />} {ok ? "Copied" : label}
    </button>
  );
}

function FaqBody({ artefact }) {
  const scriptTag = artefact.metadata?.script_tag || artefact.content;
  const items = artefact.metadata?.items || [];
  return (
    <div className="space-y-4" data-testid="artefact-faq">
      <div className="flex items-center justify-between">
        <div className="label-eyebrow">Generated Q&amp;As</div>
      </div>
      <div className="space-y-3">
        {items.map((it, i) => (
          <div
            key={i}
            className="border border-zinc-200 rounded-[4px] p-3 bg-[#fafafa]"
          >
            <div className="font-medium text-sm">{it.question}</div>
            <div className="text-sm text-zinc-700 mt-1">{it.answer}</div>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between">
        <div className="label-eyebrow">JSON-LD script tag</div>
        <CopyBtn value={scriptTag} label="Copy script" testid="faq-copy-script" />
      </div>
      <pre className="code-block max-h-[340px] text-[11px] whitespace-pre-wrap">
        {scriptTag}
      </pre>
    </div>
  );
}

function DiffBody({ artefact }) {
  const { before, after, word_count, rationale } = artefact.metadata || {};
  return (
    <div className="space-y-4" data-testid="artefact-rewrite">
      {rationale && (
        <div className="flex items-start gap-2 text-sm bg-[#fafafa] border border-zinc-200 p-3 rounded-[4px]">
          <Wand2 size={14} className="mt-0.5" /> {rationale}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Pane label="Before" tone="zinc">
          <div className="text-sm text-zinc-700 whitespace-pre-wrap">{before}</div>
        </Pane>
        <Pane
          label={`After · ${word_count || ""} words`}
          tone="emerald"
          action={<CopyBtn value={after || artefact.content} testid="rewrite-copy" />}
        >
          <div className="text-sm text-zinc-900 whitespace-pre-wrap">
            {after || artefact.content}
          </div>
        </Pane>
      </div>
    </div>
  );
}

function SplitBody({ artefact }) {
  const subs = artefact.metadata?.subheadings || [];
  const rationale = artefact.metadata?.rationale;
  return (
    <div className="space-y-4" data-testid="artefact-split">
      {rationale && (
        <div className="flex items-start gap-2 text-sm bg-[#fafafa] border border-zinc-200 p-3 rounded-[4px]">
          <Wand2 size={14} className="mt-0.5" /> {rationale}
        </div>
      )}
      <ol className="space-y-3">
        {subs.map((s, i) => (
          <li
            key={i}
            className="border border-zinc-200 rounded-[4px] p-4 bg-white"
          >
            <div className="flex items-center justify-between">
              <div className="mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                H3 · {String(i + 1).padStart(2, "0")}
              </div>
              {s.approx_word_count ? (
                <span className="mono text-xs text-zinc-500">
                  ~{s.approx_word_count} words
                </span>
              ) : null}
            </div>
            <div className="display mt-1 text-lg font-bold tracking-tight">
              {s.h3}
            </div>
            <p className="mt-1 text-sm text-zinc-700">{s.summary}</p>
          </li>
        ))}
      </ol>
    </div>
  );
}

function StaleBody({ artefact }) {
  const { updates = [], summary, published_at } = artefact.metadata || {};
  return (
    <div className="space-y-4" data-testid="artefact-stale">
      {summary && (
        <div className="flex items-start gap-2 text-sm bg-[#fafafa] border border-zinc-200 p-3 rounded-[4px]">
          <Wand2 size={14} className="mt-0.5" /> {summary}
        </div>
      )}
      {published_at && (
        <div className="text-xs text-zinc-500 mono">
          Last published: {published_at}
        </div>
      )}
      <ul className="space-y-3">
        {updates.map((u, i) => (
          <li key={i} className="border border-zinc-200 rounded-[4px] p-4">
            <div className="label-eyebrow">{u.area}</div>
            <div className="mt-1 font-medium text-sm">{u.action}</div>
            <p className="mt-1 text-xs text-zinc-600">{u.rationale}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}

function LlmsTxtBody({ artefact }) {
  const [tab, setTab] = useState("file");
  const guides = artefact.metadata?.deploy_guides || {};
  const platforms = Object.keys(guides);
  return (
    <div className="space-y-4" data-testid="artefact-llms">
      <div className="flex items-center gap-1 border-b border-zinc-200">
        <TabBtn active={tab === "file"} onClick={() => setTab("file")}>
          File
        </TabBtn>
        {platforms.map((p) => (
          <TabBtn key={p} active={tab === p} onClick={() => setTab(p)}>
            {platformLabel(p)}
          </TabBtn>
        ))}
      </div>
      {tab === "file" ? (
        <>
          <div className="flex items-center justify-between">
            <div className="label-eyebrow">/llms.txt</div>
            <CopyBtn value={artefact.content} testid="llms-copy-file" />
          </div>
          <pre className="code-block max-h-[340px] text-[11px] whitespace-pre-wrap">
            {artefact.content}
          </pre>
        </>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <div className="label-eyebrow">{platformLabel(tab)} · deploy</div>
            <CopyBtn value={guides[tab]} testid={`llms-copy-${tab}`} />
          </div>
          <pre className="code-block max-h-[340px] text-[11px] whitespace-pre-wrap">
            {guides[tab]}
          </pre>
        </>
      )}
    </div>
  );
}

function platformLabel(p) {
  return (
    {
      wordpress: "WordPress",
      webflow: "Webflow",
      nextjs: "Next.js",
      shopify: "Shopify",
      raw_html: "Raw HTML",
    }[p] || p
  );
}

function TabBtn({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 text-xs border-b-2 -mb-[1px] transition-colors ${
        active
          ? "border-black text-zinc-900 font-medium"
          : "border-transparent text-zinc-500 hover:text-zinc-900"
      }`}
    >
      {children}
    </button>
  );
}

function Pane({ label, tone, action, children }) {
  const toneCls =
    tone === "emerald"
      ? "border-emerald-200 bg-emerald-50/40"
      : "border-zinc-200 bg-[#fafafa]";
  return (
    <div className={`border rounded-[4px] p-3 ${toneCls}`}>
      <div className="flex items-center justify-between">
        <div className="label-eyebrow">{label}</div>
        {action}
      </div>
      <div className="mt-2">{children}</div>
    </div>
  );
}
